/**
 * Interactive tutorials — the two server calls, wrapping the shared ApiClient.
 *
 * Not a second HTTP client: it delegates to app/javascript/utils/api.ts so the
 * CSRF token keeps coming from the one place that reads the meta tag. What it
 * adds is the empty-body case, which ApiClient alone cannot express —
 * Api::V1::TutorialsController answers both actions with `head :no_content`, and
 * ApiClient.post finishes with `response.json()`, which rejects on a 204's empty
 * body. Without this wrapper every successful call would look like a failure.
 *
 * Both calls are advisory: they record that the florist has seen the welcome
 * card or finished the tour. A failure must never block or unwind the UI — the
 * worst case is the welcome card being offered again — so they resolve to a
 * boolean rather than throwing, and log the real failures.
 */

import { ApiClient } from '../utils/api'

/**
 * True if the request reached a 2xx.
 *
 * ApiClient throws three different things and only ONE of them means success:
 *
 * - an ApiError object (a plain object with a numeric `status`) — a real refusal;
 * - a `SyntaxError` — `response.json()` on the empty body of a 204, which means
 *   the write SUCCEEDED and there was simply nothing to parse;
 * - a `TypeError` from `fetch` itself — a dropped connection, offline, DNS or
 *   CORS. No response ever arrived, so the write did NOT happen.
 *
 * The test is therefore `instanceof SyntaxError`, not "has no numeric status".
 * A network failure also carries no `status` (verified: `fetch()` to a closed
 * port rejects with a bare `TypeError`), so keying on the absence of `status`
 * silently reports every offline call as a successful write.
 */
async function postExpectingNoContent(url: string): Promise<boolean> {
  try {
    await ApiClient.post(url, {})
    return true
  } catch (error: unknown) {
    // The one success case: a 2xx whose empty body failed to parse.
    if (error instanceof SyntaxError) return true

    const status = (error as { status?: unknown } | null)?.status
    if (typeof status === 'number') {
      console.warn(`[tour] ${url} refused with ${status}`)
    } else {
      console.warn(`[tour] ${url} never reached the server`, error)
    }
    return false
  }
}

/**
 * "I have seen the welcome card" — per user, not per tutorial, so it takes no
 * tour id. Sent when the florist starts OR skips the tour; either way the card
 * must not auto-show a second time.
 */
export function markWelcomeSeen(): Promise<boolean> {
  return postExpectingNoContent('/api/v1/tutorials/mark_welcome_seen')
}

/** "I finished this tour" — keyed by the registry id the server also knows. */
export function markTourCompleted(tourId: string): Promise<boolean> {
  return postExpectingNoContent(`/api/v1/tutorials/${encodeURIComponent(tourId)}/mark_completed`)
}
